import { Fragment, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router";
import { StyleguidePanel } from "./settings/StyleguidePanel";
import { BrandingPanel } from "./settings/BrandingPanel";
import { PalettePreview } from "./settings/PalettePreview";
import { normalizeHex } from "@/lib/theme-tokens";
import { Button } from "@/components/design/Button";
import { Collapse } from "@/components/design/Collapse";
import { SettingsCard } from "./settings/SettingsCard";
import { ModelPanel } from "./settings/ModelPanel";
import { StammdatenPanel, STAMMDATEN_TABS } from "./settings/StammdatenPanel";
import type { StammdatenTabId } from "./settings/StammdatenPanel";
import { FragebogenPanel } from "./settings/FragebogenPanel";
import { BulkCvImport } from "./settings/BulkCvImport";
import { BulkYamlImport } from "./settings/BulkYamlImport";
import { HrPromptPanel } from "./settings/HrPromptPanel";
import { AccessRolesPanel } from "./settings/AccessRolesPanel";
import { AccessManagementPanel } from "./settings/AccessManagementPanel";
import { AccessGroupsPanel } from "./settings/AccessGroupsPanel";
import { EmailTemplatesPanel } from "./settings/EmailTemplatesPanel";
import { LoginAssignmentPanel } from "./settings/LoginAssignmentPanel";
import { Permission, useAccessPermissions } from "@/auth/access-permissions";
import { useUnsavedEditsGate } from "@/lib/unsaved-edits-gate";
import { UnsavedChangesDialog } from "@/components/common/UnsavedChangesDialog";
import {
  DEFAULT_ACCENT,
  DEFAULT_BG,
  DEFAULT_SURFACE,
  useThemePrefs,
} from "@/stores/theme-prefs";

// Systemeinstellungen (gated by requireSystemAccess). Each functional section
// is gated by its own genesis Permission: Branding and the AI/access-config
// panels (write:access-config), Stammdaten (write:reference-data), Fragebögen
// (write:questionnaire). genesis enforces these server-side through
// principal_grants (242-access-model) — this is presentation only, same as
// every Permission check in spark. Roster import is gated by write:import; a
// duplicate feature-visibility switch would add no useful distinction on this
// already Permission-gated admin surface. The client-side Appearance/theme
// playground and the Styleguide show to anyone who can reach the surface.
// The old mock panels (Account / Notifications / Privacy / Data / Billing)
// had no backend and were removed.

interface Preset {
  hex: string;
  name: string;
  sub: string;
}

const PALETTE_PRESETS: Record<"bg" | "surface" | "accent", readonly Preset[]> = {
  bg: [
    { hex: DEFAULT_BG, name: "Pure White", sub: "Default" },
    { hex: "#FAF9F6", name: "Porcelain", sub: "kühles Off-White" },
    { hex: "#FAF7F2", name: "Off-White", sub: "warm, fast weiß" },
    { hex: "#F4EEE3", name: "Cream", sub: "wärmer" },
    { hex: "#EBE2D0", name: "Sandy Beige", sub: "gesetzter" },
    { hex: "#E5DCC7", name: "Hellbeton", sub: "Section: SAVE" },
  ],
  surface: [
    { hex: DEFAULT_SURFACE, name: "Porcelain", sub: "Default" },
    { hex: "#FFFFFF", name: "White", sub: "klar" },
    { hex: "#FAF7F2", name: "Off-White", sub: "warm" },
    { hex: "#F7F1E8", name: "Soft Cream", sub: "sanft" },
    { hex: "#EFE7D8", name: "Warm Paper", sub: "ruhig" },
    { hex: "#F1F3F0", name: "Mist", sub: "neutral" },
  ],
  accent: [
    { hex: DEFAULT_ACCENT, name: "Schwarz", sub: "Default" },
    { hex: "#FA8072", name: "Coral", sub: "freundlich" },
    { hex: "#F45D48", name: "Signal Red", sub: "stark" },
    { hex: "#E9A23B", name: "Amber", sub: "warm" },
    { hex: "#4F8F6B", name: "Sage", sub: "ruhig" },
    { hex: "#3A6EA5", name: "Blue", sub: "klar" },
    { hex: "#7A5C9E", name: "Plum", sub: "tief" },
  ],
};

// `permission: null` = visible to anyone who can reach the surface. `system`
// means any Permission accepted by requireSystemAccess.
const SYSTEM_PERMISSION_GATE = "system" as const;

const ALL_SECTIONS = [
  { id: "branding", labelId: "settings.sectionBranding", permission: Permission.WriteAccessConfig },
  { id: "hrPrompt", labelId: "settings.sectionHrPrompt", permission: Permission.WriteAccessConfig },
  { id: "emailTemplates", labelId: "settings.sectionEmailTemplates", permission: Permission.WriteAccessConfig },
  { id: "stammdaten", labelId: "settings.sectionStammdaten", permission: Permission.WriteReferenceData },
  { id: "fragebogen", labelId: "settings.sectionFragebogen", permission: Permission.WriteQuestionnaire },
  { id: "roster", labelId: "settings.sectionRoster", permission: Permission.WriteImport },
  // provision:invitee, not write:access-config: this is the HR action of the
  // config ADR §5, deliberately narrower than the config layer. genesis gates
  // the query and both mutations on the same Permission plus write access to
  // the target person, so this is presentation only.
  { id: "loginAssignment", labelId: "settings.sectionLoginAssignment", permission: Permission.ProvisionInvitee },
  { id: "accessManagement", labelId: "settings.sectionAccessManagement", permission: Permission.WriteAccessConfig },
  { id: "accessRoles", labelId: "admin.roles.title", permission: Permission.WriteAccessConfig, parent: "accessManagement" },
  { id: "accessGroups", labelId: "admin.groups.title", permission: Permission.WriteAccessConfig, parent: "accessManagement" },
  { id: "model", labelId: "settings.sectionAI", permission: SYSTEM_PERMISSION_GATE },
  { id: "styleguide", labelId: "settings.sectionStyleguide", permission: null },
] as const;

type SectionId = (typeof ALL_SECTIONS)[number]["id"];

// Retired section ids that old links still carry, mapped to where that content lives now.
const LEGACY_SECTIONS = new Map<string, SectionId>([["accessInspection", "accessManagement"], ["accessConfig", "accessRoles"]]);

export function SettingsPage() {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionGate = useUnsavedEditsGate();
  const { hasPermission } = useAccessPermissions();
  const canSystemSettings = hasPermission(Permission.WriteAccessConfig);
  const canReferenceData = hasPermission(Permission.WriteReferenceData);
  const canQuestionnaire = hasPermission(Permission.WriteQuestionnaire);
  const canProvisionInvitee = hasPermission(Permission.ProvisionInvitee);
  const canModelConfig = canSystemSettings || canReferenceData || canQuestionnaire;
  const sections = ALL_SECTIONS.filter((s) => {
    if (s.permission === SYSTEM_PERMISSION_GATE) return canModelConfig;
    return s.permission === null || hasPermission(s.permission);
  });
  const navigationSections = sections.filter((s) => !("parent" in s)).sort((a, b) =>
    intl.formatMessage({ id: a.labelId }).localeCompare(
      intl.formatMessage({ id: b.labelId }), intl.locale,
    ),
  );
  const [activeId, setActiveId] = useState<SectionId | null>(null);
  const sectionParam = searchParams.get("section");
  const requestedSectionValue = sectionParam === null ? null : LEGACY_SECTIONS.get(sectionParam) ?? sectionParam;
  const requestedSection = ALL_SECTIONS.some((section) => section.id === requestedSectionValue)
    ? requestedSectionValue as SectionId
    : null;
  // Fall back to the first visible section when nothing is selected yet or the
  // selection is no longer available to this user.
  const selected = requestedSection ?? activeId;
  const current = selected && sections.some((s) => s.id === selected) ? selected : sections[0]?.id;
  // Selected reference-data sub-type, driven by the Stammdaten sub-menu. In the URL (`tab`) so a
  // link can open one directly: the Match agent picker and `/a/:id` point at the KI-Agenten tab.
  // Leaving the section drops `tab` from the URL; the last tab is remembered here so coming back
  // lands where the admin left off rather than on the first tab.
  const [lastStammdatenTab, setLastStammdatenTab] = useState<StammdatenTabId>(STAMMDATEN_TABS[0].id);
  const requestedTab = searchParams.get("tab");
  const stammdatenTab: StammdatenTabId =
    STAMMDATEN_TABS.find((tab) => tab.id === requestedTab)?.id ?? lastStammdatenTab;

  function selectStammdatenTab(id: StammdatenTabId) {
    if (id === stammdatenTab) return;
    // A tab unmounts its editor without a pathname change, same as a section.
    sectionGate.request(() => {
      setLastStammdatenTab(id);
      const next = new URLSearchParams(searchParams);
      next.set("tab", id);
      next.delete("agentId");
      setSearchParams(next, { replace: true });
    });
  }

  function selectSection(id: SectionId, groupAction?: "create" | "members" | "roles") {
    if (id === current && !groupAction) return;
    // Sections unmount without a pathname change; use the shared gate before dropping a draft.
    sectionGate.request(() => {
      setActiveId(id);
      const next = new URLSearchParams(searchParams);
      next.set("section", id);
      next.delete("importJob");
      next.delete("groupId");
      next.delete("groupAction");
      next.delete("accessShortcut");
      next.delete("agentId");
      if (current === "stammdaten") setLastStammdatenTab(stammdatenTab);
      if (id === "stammdaten") next.set("tab", lastStammdatenTab);
      else next.delete("tab");
      if (groupAction) next.set("groupAction", groupAction);
      setSearchParams(next, { replace: true });
    });
  }

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
        {intl.formatMessage({ id: "settings.eyebrow", defaultMessage: "Account · Preferences" })}
      </div>
      <h1 id="settings-heading" className="smycles-page-title">
        {intl.formatMessage({ id: "settings.heading", defaultMessage: "System settings" })}
      </h1>
      <p className="mt-2 max-w-2xl text-smycles-body text-ink">
        {intl.formatMessage({
          id: "settings.intro",
          defaultMessage: "Branding, managed reference data, questionnaires and appearance.",
        })}
      </p>

      <section className="mt-8 grid grid-cols-1 items-start gap-6 @lg:grid-cols-[minmax(220px,280px)_minmax(0,1fr)] @lg:gap-8">
        <aside className="space-y-1">
          {navigationSections.map((s) => {
            const children = sections.filter((child) => "parent" in child && child.parent === s.id);
            const active = s.id === current || children.some((child) => child.id === current);
            return (
              <Fragment key={s.id}>
                <button
                  id={`settings-section-${s.id}`}
                  onClick={() => selectSection(s.id)}
                  aria-current={s.id === current ? "page" : undefined}
                  className={`flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-left text-smycles-body tracking-[0.18em] transition ${
                    active ? "font-medium text-ink" : "text-ink-muted hover:font-medium hover:text-ink"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                      active ? "bg-ink" : "bg-transparent"
                    }`}
                  />
                  {intl.formatMessage({ id: s.labelId })}
                </button>
                {active && children.length > 0 && (
                  <nav aria-label={intl.formatMessage({ id: s.labelId })}
                    className="mb-1 ml-5 space-y-0.5 border-l border-ink/10 pl-3">
                    {children.map((child) => <button key={child.id} id={`settings-section-${child.id}`} onClick={() => selectSection(child.id)}
                      aria-current={child.id === current ? "page" : undefined}
                      className={`block w-full rounded-md px-3 py-1.5 text-left text-smycles-meta transition ${
                        child.id === current ? "font-medium text-ink" : "text-ink-muted hover:text-ink"
                      }`}>
                      {intl.formatMessage({ id: child.labelId })}
                    </button>)}
                  </nav>
                )}
                {s.id === "stammdaten" && active ? (
                  <div className="mb-1 ml-5 space-y-0.5 border-l border-ink/10 pl-3">
                    {STAMMDATEN_TABS.map((tab) => {
                      const subActive = tab.id === stammdatenTab;
                      return (
                        <button
                          key={tab.id}
                          id={`settings-stammdaten-${tab.id}`}
                          onClick={() => selectStammdatenTab(tab.id)}
                          className={`block w-full rounded-md px-3 py-1.5 text-left text-smycles-meta transition ${
                            subActive ? "font-medium text-ink" : "text-ink-muted hover:text-ink"
                          }`}
                        >
                          {intl.formatMessage({ id: tab.labelId })}
                        </button>
                      );
                    })}
                  </div>
                ) : null}
              </Fragment>
            );
          })}
        </aside>

        <section className="space-y-6">
          {current === "branding" && canSystemSettings ? (
            <>
              <BrandingPanel />
              <AppearancePanel />
            </>
          ) : null}
          {current === "hrPrompt" && canSystemSettings ? <HrPromptPanel /> : null}
          {current === "emailTemplates" && canSystemSettings ? <EmailTemplatesPanel /> : null}
          {current === "stammdaten" && canReferenceData ? <StammdatenPanel active={stammdatenTab} /> : null}
          {current === "fragebogen" && canQuestionnaire ? <FragebogenPanel /> : null}
          {current === "roster" && hasPermission(Permission.WriteImport) ? (
            <>
              <div id="import-provisioning-hint" className="smycles-hint text-smycles-meta text-ink">
                {intl.formatMessage({
                  id: "service.page.imports.accountProvisioningHint",
                  defaultMessage:
                    "Hinweis: Diese Importe erstellen oder aktualisieren Profile. Es werden keine Zugangsdaten angelegt – importierte Personen können sich danach nicht direkt anmelden.",
                })}
              </div>
              <BulkCvImport />
              <BulkYamlImport />
            </>
          ) : null}
          {current === "loginAssignment" && canProvisionInvitee ? <LoginAssignmentPanel /> : null}
          {current === "accessManagement" && canSystemSettings ? <AccessManagementPanel
            onOpenRoles={() => selectSection("accessRoles")} onOpenGroups={(action) => selectSection("accessGroups", action)} /> : null}
          {current === "accessGroups" && canSystemSettings ? <AccessGroupsPanel /> : null}
          {current === "accessRoles" && canSystemSettings ? (
            <section aria-label={intl.formatMessage({ id: "admin.roles.title" })}>
              <AccessRolesPanel />
            </section>
          ) : null}
          {current === "model" && canModelConfig ? <ModelPanel /> : null}
          {current === "styleguide" ? <StyleguidePanel /> : null}
        </section>
      </section>
      <UnsavedChangesDialog open={sectionGate.blocked} saving={sectionGate.saving} error={sectionGate.error}
        onStay={sectionGate.stay} onDiscard={sectionGate.discardAndProceed} onSave={sectionGate.saveAndProceed} />
    </main>
  );
}

function AppearancePanel() {
  const intl = useIntl();
  const [activeColorTarget, setActiveColorTarget] = useState<"bg" | "surface" | "accent">("bg");
  // The preview is only needed while picking colours, so it stays folded away.
  const [previewOpen, setPreviewOpen] = useState(false);
  const bgColor = useThemePrefs((s) => s.bgColor);
  const setBgColor = useThemePrefs((s) => s.setBgColor);
  const accentColor = useThemePrefs((s) => s.accentColor);
  const setAccentColor = useThemePrefs((s) => s.setAccentColor);
  const surfaceColor = useThemePrefs((s) => s.surfaceColor);
  const setSurfaceColor = useThemePrefs((s) => s.setSurfaceColor);
  const resetPalette = useThemePrefs((s) => s.resetPalette);

  interface ColorTarget {
    id: "bg" | "surface" | "accent";
    label: string;
    hint: string;
    value: string;
    setValue: (v: string) => void;
    presets: readonly Preset[];
  }

  const colorTargets: readonly ColorTarget[] = [
    {
      id: "bg",
      label: intl.formatMessage({ id: "adminSettings.appearance.bgLabel", defaultMessage: "Background" }),
      hint: intl.formatMessage({ id: "adminSettings.appearance.bgHint", defaultMessage: "Base · Page" }),
      value: bgColor,
      setValue: setBgColor,
      presets: PALETTE_PRESETS.bg,
    },
    {
      id: "surface",
      label: intl.formatMessage({ id: "adminSettings.appearance.surfaceLabel", defaultMessage: "Boxes" }),
      hint: intl.formatMessage({ id: "adminSettings.appearance.surfaceHint", defaultMessage: "Cards · Surfaces" }),
      value: surfaceColor,
      setValue: setSurfaceColor,
      presets: PALETTE_PRESETS.surface,
    },
    {
      id: "accent",
      label: intl.formatMessage({ id: "adminSettings.appearance.accentLabel", defaultMessage: "Accent" }),
      hint: intl.formatMessage({ id: "adminSettings.appearance.accentHint", defaultMessage: "Accent · Buttons, highlights" }),
      value: accentColor,
      setValue: setAccentColor,
      presets: PALETTE_PRESETS.accent,
    },
  ];
  const activeTarget =
    colorTargets.find((target) => target.id === activeColorTarget) ?? colorTargets[0]!;

  return (
    <>
      <SettingsCard
        title={intl.formatMessage({ id: "adminSettings.appearance.title", defaultMessage: "Palette" })}
        sub={intl.formatMessage({
          id: "adminSettings.appearance.sub",
          defaultMessage:
            "Background, boxes and accent drive the app. Text, shades and buttons are derived from them and stay readable (WCAG AA).",
        })}
      >
        <div className="grid grid-cols-1 gap-3 @sm:grid-cols-3">
          {colorTargets.map((target) => (
            <ColorPicker
              key={target.id}
              id={`palette-target-${target.id}`}
              label={target.label}
              hint={target.hint}
              value={target.value}
              onChange={target.setValue}
              selected={activeTarget.id === target.id}
              onSelect={() => setActiveColorTarget(target.id)}
            />
          ))}
        </div>

        <div className="mt-5">
          <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
            {intl.formatMessage(
              { id: "adminSettings.appearance.presetsFor", defaultMessage: "Presets for {label}" },
              { label: activeTarget.label },
            )}
          </div>
          <div className="grid grid-cols-6 gap-2">
            {activeTarget.presets.map((p) => (
              <BgPresetButton
                key={p.hex}
                id={`palette-preset-${p.hex.replace("#", "").toLowerCase()}`}
                preset={p}
                active={p.hex.toUpperCase() === activeTarget.value.toUpperCase()}
                onClick={() => activeTarget.setValue(p.hex)}
              />
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-3"
            aria-expanded={previewOpen}
            aria-controls="palette-preview"
            onClick={() => setPreviewOpen((open) => !open)}
          >
            {intl.formatMessage(
              previewOpen
                ? { id: "adminSettings.appearance.previewHide", defaultMessage: "Hide preview" }
                : { id: "adminSettings.appearance.previewShow", defaultMessage: "Show preview" },
            )}
            <span aria-hidden="true" className={`ml-1.5 inline-block transition-transform ${previewOpen ? "rotate-90" : ""}`}>
              ›
            </span>
          </Button>
          {bgColor !== DEFAULT_BG || accentColor !== DEFAULT_ACCENT || surfaceColor !== DEFAULT_SURFACE ? (
            <button
              id="palette-reset"
              onClick={resetPalette}
              className="text-smycles-micro tracking-[0.25em] text-ink-muted transition hover:text-ink-accent"
            >
              {intl.formatMessage({ id: "adminSettings.appearance.reset", defaultMessage: "↺ Reset to default" })}
            </button>
          ) : null}
        </div>
        <Collapse open={previewOpen}>
          <div id="palette-preview" className="space-y-3 pt-4">
            <p className="text-smycles-meta text-ink-muted">
              {intl.formatMessage({
                id: "adminSettings.appearance.previewSub",
                defaultMessage: "How the palette looks in the app. Every colour below is derived from the three above.",
              })}
            </p>
            <PalettePreview />
          </div>
        </Collapse>
      </SettingsCard>
    </>
  );
}

function ColorPicker({
  id,
  label,
  hint,
  value,
  onChange,
  selected = false,
  onSelect,
}: {
  id?: string;
  label: string;
  hint: string;
  value: string;
  onChange: (next: string) => void;
  selected?: boolean;
  onSelect: () => void;
}) {
  const intl = useIntl();
  const [draft, setDraft] = useState(value);
  const [invalid, setInvalid] = useState(false);

  // Sync draft when the value prop changes from outside (e.g. preset click).
  // setState in effect is intentional - workshop's exact pattern at line 363-365.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!invalid && draft.toUpperCase() !== value.toUpperCase()) setDraft(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function commit(next: string) {
    const normalized = parseHexInput(next);
    if (normalized) {
      setInvalid(false);
      setDraft(normalized);
      if (normalized !== value) onChange(normalized);
    } else {
      setInvalid(true);
    }
  }

  return (
    <div
      id={id}
      onClick={onSelect}
      onFocusCapture={onSelect}
      data-selected={selected ? "true" : undefined}
      className={`block cursor-pointer rounded-md p-4 ring-1 transition ${
        selected
          ? "bg-ink/[0.06] smycles-selected ring-2 ring-accent-decoration"
          : "bg-surface ring-ink/10 hover:bg-ink/[0.06] hover:ring-accent-decoration/40"
      }`}
    >
      <div className="flex items-center gap-3">
        <label
          className="relative h-10 w-10 shrink-0 cursor-pointer overflow-hidden rounded-md ring-1 ring-ink/10"
          style={{ background: value }}
          title={intl.formatMessage({ id: "adminSettings.appearance.openPicker", defaultMessage: "Open colour picker" })}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => {
              setDraft(e.target.value.toUpperCase());
              onChange(e.target.value);
            }}
            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            aria-label={label}
          />
        </label>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-smycles-body font-medium">{label}</span>
          </div>
          <div className="text-smycles-label text-ink-muted">{hint}</div>
        </div>
        <input
          type="text"
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setInvalid(false);
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit(draft);
              e.currentTarget.blur();
            }
            if (e.key === "Escape") {
              setDraft(value);
              setInvalid(false);
              e.currentTarget.blur();
            }
          }}
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          placeholder="#RRGGBB"
          className={`w-24 rounded bg-surface px-2 py-1 text-right font-mono text-smycles-label tabular-nums ring-1 focus:outline-none ${
            invalid ? "ring-red-500/60" : "ring-ink/10 focus:ring-ink-ui"
          }`}
        />
      </div>
    </div>
  );
}

// Picker input: the same #rgb / #rrggbb rule the theme applies (lib/theme-tokens),
// kept in the uppercase the pickers display.
function parseHexInput(input: string): string | null {
  const hex = normalizeHex(input, "");
  return hex ? hex.toUpperCase() : null;
}

function BgPresetButton({
  id,
  preset,
  active,
  onClick,
}: {
  id?: string;
  preset: Preset;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      id={id}
      onClick={onClick}
      title={`${preset.name} · ${preset.hex}`}
      aria-pressed={active}
      className={`rounded-md bg-surface p-2 text-left ring-1 transition ${
        active ? "smycles-selected ring-2 ring-accent-decoration" : "ring-ink/10 hover:ring-accent-decoration/40"
      }`}
    >
      <span
        className="block aspect-[3/1] w-full rounded-sm ring-1 ring-ink/5"
        style={{ background: preset.hex }}
      />
      <div className="mt-2 text-smycles-micro tracking-[0.18em] text-ink">{preset.name}</div>
      <div className="font-mono text-smycles-micro tabular-nums text-ink-muted">{preset.hex}</div>
      <div className="text-smycles-micro leading-tight text-ink-muted">{preset.sub}</div>
    </button>
  );
}
